import { el, on } from './dom.js';

export interface ModalOptions {
  title: string;
  body: Node;
  actions?: Array<{ label: string; kind?: 'primary' | 'ghost' | 'danger'; onClick(): void | boolean }>;
  /** Whether the scrim and Escape can close it. */
  dismissable?: boolean;
  /** A wider panel, for things that are looked at rather than read. */
  wide?: boolean;
  /** A whole page on a phone, a wide panel elsewhere: for Settings. */
  page?: boolean;
  onClose?(): void;
}

/**
 * A focus-trapping dialog.
 *
 * Hand-rolled rather than `<dialog>` because Safari's native dialog still
 * disagrees with the rest on scroll locking and backdrop styling, and a game
 * that opens modals mid-drag cannot afford platform surprises.
 */
export function openModal(opts: ModalOptions): () => void {
  const previous = document.activeElement as HTMLElement | null;

  const foot = el('div', { class: 'modal__foot' });
  for (const a of opts.actions ?? []) {
    const cls = a.kind === 'primary' ? 'btn btn--primary'
      : a.kind === 'danger' ? 'btn btn--danger'
      : a.kind === 'ghost' ? 'btn btn--ghost' : 'btn';
    const b = el('button', { class: cls, type: 'button', text: a.label });
    on(b, 'click', () => { if (a.onClick() !== false) close(); });
    foot.append(b);
  }

  const closeBtn = el('button', {
    class: 'btn btn--icon btn--ghost', type: 'button',
    'aria-label': 'Close', text: '×',
  });
  const panel = el('div', {
    class: 'modal panel' + (opts.wide || opts.page ? ' modal--wide' : '') + (opts.page ? ' modal--page' : ''),
    role: 'dialog', 'aria-modal': 'true',
  },
    el('div', { class: 'modal__head' },
      el('h2', { text: opts.title }),
      el('div', { class: 'topbar__spacer' }),
      opts.dismissable === false ? null : closeBtn),
    el('div', { class: 'modal__body scroll' }, opts.body),
    (opts.actions?.length ?? 0) > 0 ? foot : null);

  const scrim = el('div', { class: 'modal__scrim' }, panel);
  document.body.append(scrim);

  const focusables = (): HTMLElement[] =>
    [...panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((e) => !e.hasAttribute('disabled') && e.offsetParent !== null);

  focusables()[0]?.focus();

  const offKey = on(document, 'keydown', (ev) => {
    const e = ev as KeyboardEvent;
    if (e.key === 'Escape' && opts.dismissable !== false) { e.preventDefault(); close(); }
    if (e.key !== 'Tab') return;
    const items = focusables();
    if (items.length === 0) return;
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  on(closeBtn, 'click', () => close());
  on(scrim, 'pointerdown', (e) => {
    if (e.target === scrim && opts.dismissable !== false) close();
  });

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    offKey();
    scrim.remove();
    previous?.focus?.();
    opts.onClose?.();
  }
  return close;
}

/** Simple yes/no. Resolves false if dismissed. */
export function confirmModal(
  title: string, message: string, confirmLabel = 'Confirm',
): Promise<boolean> {
  return new Promise((resolve) => {
    let answered = false;
    openModal({
      title,
      body: el('p', { text: message, style: 'margin:0;color:var(--text-1)' }),
      actions: [
        { label: 'Cancel', kind: 'ghost', onClick: () => { answered = true; resolve(false); } },
        { label: confirmLabel, kind: 'primary', onClick: () => { answered = true; resolve(true); } },
      ],
      onClose: () => { if (!answered) resolve(false); },
    });
  });
}
